import React, { useCallback, useEffect, useRef, useState } from "react";
import { REPORT_SECTIONS } from "../../classes/constants/reportConstants";
import { ROUTE_TYPES } from "../../classes/constants/routeConstants";
import IconsEnum from "../../classes/enums/IconsEnum";
import { SnackbarType } from "../../classes/enums/SnackbarTypeEnum";
import useTranslation from "../../hooks/i18n/useTranslation";
import useRemoteViewActions from "../../hooks/useRemoteViewActions";
import { useSnackbar } from "../../hooks/useSnackbar";
import ValidationHistoryButtons from "../../modules/validation/validationHistory/ValidationHistoryButtons";
import ContrastAnalysisService from "../../services/ContrastAnalysisService";
import { updateInstanceDisplaySettings } from "../../services/InstancesService";
import simpleIntegratedScreenshotService from "../../services/screenshots/IntegratedScreenshotService";
import ButtonWithIcon from "../button/ButtonWithIcon";
import IconButton from "../iconButton/IconButton";
import RangeSlider from "../rangeSlider/RangeSlider";
import "./ImageViewer.css";

const MAX_SLICE_VALUE = 1;
const MIN_SLICE_VALUE = 0;

function ImageViewer({
  children,
  className = "",
  onClose,
  onCameraClick,
  onBrightnessClick,
  onSagittalPlaneClick,
  onChangeSlice,
  onContentLoad,
  onLockToggle,
  onGetValidationHistoryState,
  hasUndoRedo = false,
  isDataFusionActive = false,
  contrastOptions = {},
  step = ROUTE_TYPES.CHECK.toLowerCase(),
  viewType = null,
  remoteRenderClient = null,
  viewId = null,
  planId = null,
  viewData = null,
  instance = null,
  onZoomIn = () => {},
  onZoomOut = () => {},
  // Display settings persistence props
  instanceId = null,
  displaySettingsViewType = null,
  persistedDisplaySettings = null,
  onDisplaySettingsUpdate = null,
}) {
  const { t } = useTranslation();
  const [contrastInfo, setContrastInfo] = useState({
    topRight: { contrast: "light", isValid: false },
    bottomRight: { contrast: "light", isValid: false },
  });

  const [isCapturing, setIsCapturing] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [currentSlice, setCurrentSlice] = useState(MAX_SLICE_VALUE / 2);
  const [currentRemoteSlice, setCurrentRemoteSlice] = useState(null);

  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const flashRef = useRef(null);
  const contrastOptionsRef = useRef(contrastOptions);

  const resolvedReportSection = REPORT_SECTIONS.ANNEX;

  const brightnessControlsRef = useRef(null);
  const brightnessButtonRef = useRef(null);

  const [showConstrastBrightnessControls, setShowConstrastBrightnessControls] =
    useState(false);
  const [brightness, setBrightness] = useState(0.5);
  const [contrast, setContrast] = useState(0.5);

  const [isLocked, setIsLocked] = useState(false);
  const [zoomScale, setZoomScale] = useState(1.0);
  const previousZoomScaleRef = useRef(1.0);

  // Track initial values when brightness controls open (for save-on-close)
  const initialDisplaySettingsRef = useRef({ brightness: 0.5, contrast: 0.5 });
  const hasAppliedPersistedSettingsRef = useRef(false);

  const { showSnackbar } = useSnackbar();

  const percentageToScale = useCallback(percentage => percentage / 100, []);
  const scaleToPercentage = useCallback(scale => scale * 100, []);

  const handleZoomChange = useCallback(
    newPercentage => {
      const numPercentage =
        typeof newPercentage === "string"
          ? parseFloat(newPercentage)
          : newPercentage;
      const clampedPercentage = Math.max(0.03, Math.min(300, numPercentage));
      const newScale = percentageToScale(clampedPercentage);
      const previousScale = previousZoomScaleRef.current;

      setZoomScale(newScale);
      previousZoomScaleRef.current = newScale;

      if (newScale > previousScale && onZoomIn)
        onZoomIn(newScale, clampedPercentage);
      if (newScale < previousScale && onZoomOut)
        onZoomOut(newScale, clampedPercentage);
    },
    [percentageToScale, onZoomIn, onZoomOut]
  );

  const handleZoomInClick = useCallback(() => {
    const currentPercentage = scaleToPercentage(zoomScale);
    const newPercentage = Math.min(300, currentPercentage + 10);
    handleZoomChange(newPercentage);
  }, [zoomScale, scaleToPercentage, handleZoomChange]);

  const handleZoomOutClick = useCallback(() => {
    const currentPercentage = scaleToPercentage(zoomScale);
    const newPercentage = Math.max(0.03, currentPercentage - 10);
    handleZoomChange(newPercentage);
  }, [zoomScale, scaleToPercentage, handleZoomChange]);

  const handleZoomWheel = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const currentPercentage = scaleToPercentage(zoomScale);
      const scrollDelta = -e.deltaY;
      const zoomStep = Math.abs(scrollDelta) > 50 ? 5 : 2;
      const deltaPercentage = scrollDelta > 0 ? zoomStep : -zoomStep;
      const newPercentage = Math.max(
        0.03,
        Math.min(300, currentPercentage + deltaPercentage)
      );

      handleZoomChange(newPercentage);
    },
    [zoomScale, scaleToPercentage, handleZoomChange]
  );

  const {
    getBrightnessAndContrast,
    setBrightnessAndContrast,
    resetView: resetViewHook,
    undoValidation: undoValidationHook,
    redoValidation: redoValidationHook,
    resetValidation: resetValidationHook,
    getValidationHistoryState,
  } = useRemoteViewActions(planId);

  const undoValidation = useCallback(async () => {
    return await undoValidationHook(viewId);
  }, [undoValidationHook, viewId]);

  const redoValidation = useCallback(async () => {
    return await redoValidationHook(viewId);
  }, [redoValidationHook, viewId]);

  const resetValidation = useCallback(async () => {
    return await resetValidationHook();
  }, [resetValidationHook]);

  const getViewValidationHistoryState = useCallback(async () => {
    return await getValidationHistoryState(viewId);
  }, [getValidationHistoryState, viewId]);

  const fetchCurrentBrightnessContrast = useCallback(async () => {
    try {
      if (!viewId || !remoteRenderClient)
        return { brightness: 0.5, contrast: 0.5 };
      const result = await getBrightnessAndContrast(viewId);

      if (result && !result.error) {
        let brightness = result.brightness;
        let contrast = result.contrast;

        const needsCorrection =
          brightness === undefined ||
          contrast === undefined ||
          isNaN(brightness) ||
          isNaN(contrast);

        if (needsCorrection) {
          console.warn(
            "Backend returned invalid brightness/contrast values, using defaults"
          );
          return { brightness: 0.5, contrast: 0.5 };
        }

        return {
          brightness,
          contrast,
        };
      }

      return { brightness: 0.5, contrast: 0.5 };
    } catch (error) {
      console.error("Error fetching brightness/contrast:", error);
      return { brightness: 0.5, contrast: 0.5 };
    }
  }, [viewId, remoteRenderClient, getBrightnessAndContrast]);

  const handleBrightnessChange = useCallback(
    async newBrightness => {
      setBrightness(newBrightness);

      try {
        if (viewId && remoteRenderClient) {
          await setBrightnessAndContrast(viewId, newBrightness, contrast);
        }
      } catch (error) {}
    },
    [viewId, remoteRenderClient, setBrightnessAndContrast, contrast]
  );

  const handleContrastChange = useCallback(
    async newContrast => {
      setContrast(newContrast);

      try {
        if (viewId && remoteRenderClient) {
          await setBrightnessAndContrast(viewId, brightness, newContrast);
        }
      } catch (error) {}
    },
    [viewId, remoteRenderClient, setBrightnessAndContrast, brightness]
  );

  const handleBrightnessAndContrastClick = async () => {
    const newShowState = !showConstrastBrightnessControls;
    setShowConstrastBrightnessControls(newShowState);

    if (newShowState) {
      try {
        const values = await fetchCurrentBrightnessContrast();
        setBrightness(values.brightness);
        setContrast(values.contrast);
        // Store initial values for comparison on close
        initialDisplaySettingsRef.current = {
          brightness: values.brightness,
          contrast: values.contrast,
        };
      } catch (error) {}
    }

    if (onBrightnessClick) onBrightnessClick(contrastInfo);
  };

  const handleLockToggle = useCallback(async () => {
    const newLockState = !isLocked;
    setIsLocked(newLockState);

    if (onLockToggle) {
      try {
        await onLockToggle(newLockState);
      } catch (error) {
        setIsLocked(!newLockState);
      }
    }
  }, [isLocked, onLockToggle]);

  const handleResetViewClick = useCallback(async () => {
    try {
      if (viewId && resetViewHook) {
        await resetViewHook(viewId);
      }
    } catch (error) {
      console.error("Error resetting view:", error);
    }
  }, [viewId, resetViewHook]);

  const analyzeImage = useCallback(img => {
    if (img && img.complete && img.naturalWidth > 0) {
      const results = ContrastAnalysisService.analyzeImageViewer(
        img,
        contrastOptionsRef.current
      );
      setContrastInfo(results);
    }
  }, []);

  const handleImageLoad = e => {
    if (onContentLoad) onContentLoad(e);

    setImageSrc(e.target.src);
    analyzeImage(e.target);
  };

  const handleAnimationEnd = () => {
    setIsFlashing(false);
  };

  const handleSliceChange = useCallback(
    sliceIndex => {
      setCurrentSlice(sliceIndex);
      if (onChangeSlice)
        onChangeSlice(sliceIndex, step.toLowerCase(), viewType);
    },
    [onChangeSlice, step, viewType]
  );

  const handleCameraClick = async () => {
    if (isCapturing) return;

    try {
      setIsCapturing(true);
      setIsFlashing(true);

      if (!containerRef.current) return;

      await new Promise(resolve => setTimeout(resolve, 50));

      const extractOrientation = viewName => {
        if (!viewName) return null;
        const lower = viewName.toLowerCase();
        if (lower.includes("sagittal") || lower.includes("lateral"))
          return "sagittal";
        if (lower.includes("coronal") || lower.includes("front"))
          return "coronal";
        if (lower.includes("axial")) return "axial";
        return null;
      };

      const actualSliceNumber =
        currentRemoteSlice !== null
          ? Math.round(currentRemoteSlice)
          : viewData?.sliceData?.current || null;

      const captureDetails =
        await simpleIntegratedScreenshotService.captureScreenshot(
          containerRef.current,
          {
            debug: true,
            showNotification: false,
            persistToReport: true,
            reportSection: resolvedReportSection,
            reportMetadata: {
              source: "image-viewer",
              planId,
              viewId,
              viewType,
              step,
              reportSection: resolvedReportSection,

              actualSliceNumber,

              viewName: viewData?.viewName || viewType,
              sliceData: viewData?.sliceData
                ? {
                    min: viewData.sliceData.min,
                    max: viewData.sliceData.max,
                    current: viewData.sliceData.current,
                  }
                : null,

              instanceId: instance?._id || null,
              modality: instance?.modality || null,
              orientation: extractOrientation(viewData?.viewName || viewType),

              instanceMetadata: instance
                ? {
                    file_name: instance.file_name,
                    date: instance.date,
                    series_description: instance.series_description,
                  }
                : null,
            },
          }
        );

      if (onCameraClick) onCameraClick(captureDetails);

      showSnackbar(SnackbarType.SUCCESS, "Screenshot captured successfully", {
        duration: 5000,
      });
    } catch (error) {
    } finally {
      setTimeout(() => {
        setIsCapturing(false);
      }, 300);
    }
  };

  // Save display settings to database
  const saveDisplaySettings = useCallback(async () => {
    if (!instanceId || !displaySettingsViewType) {
      return;
    }

    const hasChanges =
      brightness !== initialDisplaySettingsRef.current.brightness ||
      contrast !== initialDisplaySettingsRef.current.contrast;

    if (!hasChanges) {
      return;
    }

    try {
      await updateInstanceDisplaySettings(
        instanceId,
        displaySettingsViewType,
        brightness,
        contrast
      );
      if (onDisplaySettingsUpdate) {
        onDisplaySettingsUpdate(
          instanceId,
          displaySettingsViewType,
          brightness,
          contrast
        );
      }
    } catch (error) {
      console.error("Failed to save display settings:", error);
    }
  }, [
    instanceId,
    displaySettingsViewType,
    brightness,
    contrast,
    onDisplaySettingsUpdate,
  ]);

  useEffect(() => {
    const handleClickOutside = async event => {
      if (
        showConstrastBrightnessControls &&
        brightnessControlsRef.current &&
        brightnessButtonRef.current &&
        !brightnessControlsRef.current.contains(event.target) &&
        !brightnessButtonRef.current.contains(event.target)
      ) {
        // Save on close
        await saveDisplaySettings();
        setShowConstrastBrightnessControls(false);
      }
    };

    if (showConstrastBrightnessControls)
      document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showConstrastBrightnessControls, saveDisplaySettings]);

  useEffect(() => {
    const checkAndAnalyzeImage = () => {
      if (
        imageRef.current &&
        imageRef.current.complete &&
        imageRef.current.naturalWidth > 0
      ) {
        if (imageRef.current.src) setImageSrc(imageRef.current.src);
        analyzeImage(imageRef.current);
      }
    };

    checkAndAnalyzeImage();

    const imgElement = imageRef.current;
    if (imgElement) imgElement.addEventListener("load", checkAndAnalyzeImage);
    return () => {
      if (imgElement)
        imgElement.removeEventListener("load", checkAndAnalyzeImage);
    };
  }, [analyzeImage]);

  useEffect(() => {
    contrastOptionsRef.current = contrastOptions;
  }, [contrastOptions]);

  useEffect(() => {
    const initializeBrightnessContrast = async () => {
      if (!viewId || !remoteRenderClient) {
        return;
      }

      try {
        const values = await fetchCurrentBrightnessContrast();
        setBrightness(values.brightness);
        setContrast(values.contrast);
      } catch (error) {}
    };

    initializeBrightnessContrast();
  }, [viewId, remoteRenderClient, fetchCurrentBrightnessContrast]);

  // Apply persisted display settings from database
  useEffect(() => {
    const applyPersistedSettings = async () => {
      if (
        !persistedDisplaySettings ||
        !viewId ||
        !remoteRenderClient ||
        hasAppliedPersistedSettingsRef.current
      ) {
        return;
      }

      const { brightness: persistedBrightness, contrast: persistedContrast } =
        persistedDisplaySettings;

      if (
        persistedBrightness === undefined ||
        persistedContrast === undefined
      ) {
        return;
      }

      try {
        await setBrightnessAndContrast(
          viewId,
          persistedBrightness,
          persistedContrast
        );
        setBrightness(persistedBrightness);
        setContrast(persistedContrast);
        hasAppliedPersistedSettingsRef.current = true;
      } catch (error) {
        console.error("Failed to apply persisted display settings:", error);
      }
    };

    applyPersistedSettings();
  }, [
    persistedDisplaySettings,
    viewId,
    remoteRenderClient,
    setBrightnessAndContrast,
  ]);

  const processedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      if (child.type === "img") {
        return React.cloneElement(child, {
          onLoad: handleImageLoad,
          ref: imageRef,
        });
      }

      if (child.type?.name === "RemoteViewer") {
        return React.cloneElement(child, {
          onSliceChange: setCurrentRemoteSlice,
        });
      }
    }
    return child;
  });

  return (
    <div ref={containerRef} className={`image-viewer ${className}`}>
      {imageSrc && (
        <div
          className="image-viewer-background"
          style={{ backgroundImage: `url(${imageSrc})` }}
        />
      )}

      <div
        ref={flashRef}
        className={`image-viewer-flash ${isFlashing ? "active" : ""}`}
        onAnimationEnd={handleAnimationEnd}
      />

      {hasUndoRedo && (
        <ValidationHistoryButtons
          onUndo={undoValidation}
          onRedo={redoValidation}
          onReset={resetValidation}
          getHistoryState={
            onGetValidationHistoryState || getViewValidationHistoryState
          }
          viewId={viewId}
          contrastClass={contrastInfo.topRight.contrast}
        />
      )}

      {onLockToggle && (
        <div className="image-viewer-lock">
          <ButtonWithIcon
            value={isLocked ? "Unlock image" : "Lock image"}
            icon={isLocked ? IconsEnum.LOCK : IconsEnum.UNLOCK}
            onClick={handleLockToggle}
            isLightBlue={false}
            className={`image-viewer-button-no-bg image-viewer-icon-${contrastInfo.topRight.contrast}`}
          />
        </div>
      )}

      {onClose && (
        <div className="image-viewer-close">
          <IconButton
            icon={IconsEnum.CLOSE}
            onClick={onClose}
            isCircle={true}
            noBgColor={true}
            title={t("43")}
            className={`image-viewer-icon-${contrastInfo.topRight.contrast}`}
          />
        </div>
      )}

      <div className="image-viewer-zoom-controls">
        {/* <div
          className="image-viewer-zoom-container"
          onWheel={handleZoomWheel}
          style={{ cursor: "pointer" }}
        >
          <button
            className="image-viewer-zoom-icon-button"
            onClick={handleZoomInClick}
            title="Zoom in"
            type="button"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`image-viewer-zoom-icon image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
            >
              <circle
                cx="7"
                cy="7"
                r="4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M10 10L13 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M7 4V10M4 7H10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="image-viewer-zoom-slider-wrapper">
            <RangeSlider
              min={0.03}
              max={300}
              step={0.1}
              rangeValue={Number(scaleToPercentage(zoomScale))}
              onRangeValueChanged={handleZoomChange}
              className="image-viewer-zoom-slider"
            />
            <div
              className={`image-viewer-zoom-label image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
            >
              {Math.round(scaleToPercentage(zoomScale))}%
            </div>
          </div>

          <button
            className="image-viewer-zoom-icon-button"
            onClick={handleZoomOutClick}
            title="Zoom out"
            type="button"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`image-viewer-zoom-icon image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
            >
              <circle
                cx="7"
                cy="7"
                r="4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M10 10L13 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M4 7H10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div> */}
      </div>

      <div className="image-viewer-controls">
        {isDataFusionActive && onChangeSlice && (
          <RangeSlider
            min={MIN_SLICE_VALUE}
            max={MAX_SLICE_VALUE}
            step={0.1}
            rangeValue={Number(currentSlice)}
            onRangeValueChanged={handleSliceChange}
            className={`image-viewer-slider`}
          />
        )}

        {onSagittalPlaneClick && (
          <IconButton
            icon={IconsEnum.SAGITTALPLANE}
            onClick={onSagittalPlaneClick}
            isCircle={true}
            noBgColor={true}
            title={t("449")}
            className={`image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
          />
        )}
        {viewId && resetViewHook && (
          <IconButton
            icon={IconsEnum.RESETVIEW}
            onClick={handleResetViewClick}
            isCircle={true}
            noBgColor={true}
            title={t("450")}
            className={`image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
          />
        )}
        {onBrightnessClick && (
          <div ref={brightnessButtonRef}>
            <IconButton
              icon={IconsEnum.BRIGHTNESS}
              onClick={handleBrightnessAndContrastClick}
              isCircle={true}
              noBgColor={true}
              title={t("451")}
              className={`image-viewer-icon-${contrastInfo.bottomRight.contrast}`}
            />
          </div>
        )}

        <IconButton
          icon={IconsEnum.CAMERA}
          onClick={isCapturing ? undefined : handleCameraClick}
          isCircle={true}
          noBgColor={true}
          title={t("452")}
          className={`image-viewer-icon-${contrastInfo.bottomRight.contrast} ${
            isCapturing ? "image-viewer-icon-disabled" : ""
          }`}
        />
      </div>

      {showConstrastBrightnessControls && onBrightnessClick && (
        <div
          ref={brightnessControlsRef}
          className="brightness-contrast-controls"
        >
          <div className="brightness-contrast-wrapper">
            <RangeSlider
              min={0}
              max={1}
              step={0.01}
              rangeValue={1 - brightness}
              onRangeValueChanged={v => handleBrightnessChange(1 - v)}
              className="brightness-contrast-slider"
            />
            <div className="App-text-medium brightness-contrast-label">B</div>
          </div>
          <div className="brightness-contrast-wrapper">
            <RangeSlider
              min={0}
              max={1}
              step={0.01}
              rangeValue={1 - contrast}
              onRangeValueChanged={v => handleContrastChange(1 - v)}
              className="brightness-contrast-slider"
            />
            <div className="App-text-medium brightness-contrast-label">C</div>
          </div>
        </div>
      )}

      <div
        className="image-viewer-content"
        style={{
          transformOrigin: "center center",
        }}
      >
        {processedChildren}
      </div>
    </div>
  );
}

export default ImageViewer;
